import { useRef, useState } from "react";

import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Layout,
  message,
  Row,
  Space,
  Table,
} from "antd";

import { PrinterOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";
import { sumBy } from "lodash";
import moment from "moment";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import DatePickerFieldGroup from "../../commons/DatePickerFieldGroup";
import { onChange } from "../../utils/form_utilities";
import numberFormat from "../../utils/numberFormat";
import ReportHeading from "../../utils/ReportHeading";
import { addKeysToArray } from "../../utils/utilities";
import { smallFormItemLayout } from "./../../utils/Layouts";

const { Content } = Layout;

const url = "/api/billing-statement/";
const title = "Customer Aging Summary";

const initialValues = {
  date: moment(),
};
export default function CustomerAgingSummary() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const [loading_state, setLoadingState] = useState({
    search: false,
    download: false,
  });
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [options, setOptions] = useState({
    customers: [],
  });

  const report = useRef(null);

  const records_column = [
    {
      title: "Account",
      dataIndex: ["name"],
    },
    {
      title: "Current",
      dataIndex: "Current",
      align: "right",
      width: 100,
      render: (amount, record) => <span>{numberFormat(amount)}</span>,
    },
    {
      title: "1-30",
      dataIndex: "1-30",
      align: "right",
      width: 100,
      render: (amount, record) => <span>{numberFormat(amount)}</span>,
    },
    {
      title: "31-60",
      dataIndex: "31-60",
      align: "right",
      width: 100,
      render: (amount, record) => <span>{numberFormat(amount)}</span>,
    },
    {
      title: "61-90",
      dataIndex: "61-90",
      align: "right",
      width: 100,
      render: (amount, record) => <span>{numberFormat(amount)}</span>,
    },
    {
      title: ">90",
      dataIndex: ">90",
      align: "right",
      width: 100,
      render: (amount, record) => <span>{numberFormat(amount)}</span>,
    },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
      width: 100,
      render: (amount, record) => <span>{numberFormat(amount)}</span>,
    },
  ];

  const getReport = useCallback(({ ...form_data }) => {
    if (form_data.date) {
      setLoadingState((prevState) => ({
        ...prevState,
        search: !form_data?.is_excel,
        download: form_data?.is_excel,
      }));
      axios
        .post(`${url}customer-aging-summary`, form_data)
        .then((response) => {
          if (response.data) {
            if (!form_data?.is_excel) {
              setLoadingState((prevState) => ({
                ...prevState,
                search: false,
              }));
              return setRecords(response.data);
            }
            window.open(`/public/reports/${response.data.filename}`);
            setLoadingState((prevState) => ({
              ...prevState,
              download: false,
            }));
          }
        })
        .catch((err) => {
          setLoadingState((prevState) => ({
            ...prevState,
            search: false,
            download: false,
          }));
          message.error("There was an error processing your request");
        });
    } else {
      message.error("Please select a date");
    }
  }, []);

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item className="text-color-yellow">
              {title}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          padding: 24,
          minHeight: 280,
          borderRadius: 10,
        }}
      >
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <DatePickerFieldGroup
              label="Date"
              name="date"
              value={state.date}
              onChange={(value) => {
                onChange({
                  key: "date",
                  value: value,
                  setState,
                });
              }}
              error={errors.date}
              formItemLayout={smallFormItemLayout}
            />
          </Col>
        </Row>

        <Row>
          <Col span={8} offset={4}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  disabled={loading_state.search}
                  loading={loading_state.search}
                  onClick={() => {
                    getReport({ ...state });
                  }}
                >
                  Search
                </Button>
                <Button
                  icon={<TableOutlined />}
                  onClick={() => {
                    getReport({ ...state, is_excel: true });
                  }}
                  disabled={loading_state.download}
                  loading={loading_state.download}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div className="report-heading">
            <ReportHeading />
            <span className="has-text-weight-bold">{title}</span>
            <br />
            {moment(state.date).format("ll")} <br />
            Printed By : {auth.user.name} <br />
            Date/Time Printed : {moment().format("LLL")}
          </div>

          <Table
            size="small"
            dataSource={addKeysToArray([
              ...records,
              {
                footer: 1,
                Current: sumBy(records, (o) => o["Current"]),
                "1-7": sumBy(records, (o) => o["1-7"]),
                "8-15": sumBy(records, (o) => o["8-15"]),
                "16-30": sumBy(records, (o) => o["16-30"]),
                "31-45": sumBy(records, (o) => o["31-45"]),
                "46-60": sumBy(records, (o) => o["46-60"]),
                ">60": sumBy(records, (o) => o[">60"]),
                total: sumBy(records, (o) => o["total"]),
              },
            ])}
            columns={records_column}
            pagination={false}
            rowClassName={(record, index) => {
              if (record.footer === 1) {
                return "footer-summary has-text-weight-bold";
              }
            }}
          />
        </div>
      </div>
    </Content>
  );
}
